import { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  ContentSwitcher,
  Switch as CarbonSwitch,
  Modal,
} from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { parseMomentUtc, getLocalTimezoneAbbreviation } from 'utils/dates';
import { useAtom, useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import {
  batchDetailsAtom,
  selectedBatchIdAtom,
  deleteBatchMutationAtom,
} from '../atoms';
import { BatchSummaryCards } from './summary-cards';
import {
  statusTagColors,
  statusLabels,
  batchModeLabels,
  batchModeTagColors,
  documentTypeFilterLabels,
  provisioningStatusLabels,
  type ProvisioningBatch,
  type BatchStatus,
  type BatchMode,
  type DocumentTypeFilter,
  type ProvisioningStatusFilter,
} from '../types';
import styles from '../page.module.scss';
import { SubmissionsTable } from './submissions-table';
import { AllDocumentsTable } from './all-documents-table';

interface SearchParams {
  provisioning_status?: string;
  document_type?: string;
  prompt_ids?: number[];
  provisioned_after?: string;
  provisioned_before?: string;
}

const FilterCriteria: FC<{ batch: ProvisioningBatch }> = ({ batch }) => {
  const batchParams = (batch.parameters as SearchParams) || {};
  const mode = (batch.mode as BatchMode) || 'provision';

  return (
    <div className={styles.filterCriteria}>
      <h4 className={styles.filterCriteriaTitle}>Filter Criteria</h4>
      <div className={styles.filterCriteriaTags}>
        <Tag type="gray" size="sm">
          Mode: {batchModeLabels[mode] || mode}
        </Tag>

        {batch.stateName && (
          <Tag type="gray" size="sm">
            State: {batch.stateName}
          </Tag>
        )}

        <Tag type="gray" size="sm">
          {batch.countyName ? `County: ${batch.countyName}` : 'All Counties'}
        </Tag>

        {batchParams.provisioning_status && (
          <Tag type="gray" size="sm">
            {provisioningStatusLabels[
              batchParams.provisioning_status as ProvisioningStatusFilter
            ] || batchParams.provisioning_status}
          </Tag>
        )}

        {batchParams.document_type && (
          <Tag type="gray" size="sm">
            Type:{' '}
            {documentTypeFilterLabels[
              batchParams.document_type as DocumentTypeFilter
            ] || batchParams.document_type}
          </Tag>
        )}

        {batchParams.prompt_ids && batchParams.prompt_ids.length > 0 && (
          <Tag type="gray" size="sm">
            Prompt IDs: {batchParams.prompt_ids.join(', ')}
          </Tag>
        )}

        {batchParams.provisioned_after && (
          <Tag type="gray" size="sm">
            Provisioned After: {batchParams.provisioned_after}
          </Tag>
        )}

        {batchParams.provisioned_before && (
          <Tag type="gray" size="sm">
            Provisioned Before: {batchParams.provisioned_before}
          </Tag>
        )}
      </div>
    </div>
  );
};

export const BatchDetailPage: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const batchId = id !== undefined ? parseInt(id, 10) : NaN;
  const isInvalidId = Number.isNaN(batchId);

  // View toggle: 0 = Batch View, 1 = Document View
  const [viewIndex, setViewIndex] = useState(0);

  // Delete batch state
  const [deleteState, deleteBatch] = useAtom(deleteBatchMutationAtom);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Set selected batch ID to trigger atom fetching
  const setSelectedBatchId = useSetAtom(selectedBatchIdAtom);
  useEffect(() => {
    if (!isInvalidId) setSelectedBatchId(batchId);
    return () => setSelectedBatchId(null);
  }, [batchId, isInvalidId, setSelectedBatchId]);

  const [batchLoadable] = useAtom(batchDetailsAtom);

  // Extract batch data
  const batch = useMemo(() => {
    if (batchLoadable.state !== 'hasData' || !batchLoadable.data) return null;
    return batchLoadable.data.batch;
  }, [batchLoadable]);

  const isBatchLoading = batchLoadable.state === 'loading';
  const hasBatchError = batchLoadable.state === 'hasError';

  const handleDelete = async () => {
    const success = await deleteBatch(batchId);
    if (success) {
      setShowDeleteModal(false);
      history.push('/admin/provisioning_batches');
    }
  };

  const backButton = (
    <Button kind="ghost" size="sm" onClick={() => history.goBack()}>
      Back
    </Button>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      <Button
        kind="danger--ghost"
        size="sm"
        renderIcon={TrashCan}
        onClick={() => setShowDeleteModal(true)}
      >
        Delete
      </Button>
      {backButton}
    </div>
  );

  if (isInvalidId) {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="Batch Not Found"
          actions={backButton}
        />
        <div className={styles.sectionHeader}>
          <div className={styles.sectionDescription}>
            Invalid batch ID in URL.
          </div>
        </div>
      </>
    );
  }

  if (isBatchLoading) {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title={`Batch #${batchId}`}
          actions={backButton}
        />
        <InlineLoading description="Loading batch details..." />
      </>
    );
  }

  if (hasBatchError || !batch) {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="Batch Not Found"
          actions={backButton}
        />
        <div className={styles.sectionHeader}>
          <div className={styles.sectionDescription}>
            {hasBatchError
              ? 'Failed to load batch details. Check your connection and try again.'
              : `Batch #${batchId} not found.`}
          </div>
        </div>
      </>
    );
  }

  const elapsed = batch.startedAt
    ? batch.completedAt
      ? `${Math.round(
          (new Date(batch.completedAt).getTime() -
            new Date(batch.startedAt).getTime()) /
            1000 /
            60
        )} min`
      : 'Running...'
    : '--';

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title={`Batch #${batch.id}`}
        actions={headerActions}
        titleDetail={
          <>
            <Tag type={statusTagColors[batch.status as BatchStatus]} size="sm">
              {statusLabels[batch.status as BatchStatus]}
            </Tag>
            {batch.mode && (
              <Tag
                type={batchModeTagColors[batch.mode as BatchMode] || 'gray'}
                size="sm"
              >
                {batchModeLabels[batch.mode as BatchMode] || batch.mode}
              </Tag>
            )}
          </>
        }
      />

      <div className={styles.summaryCardsWrapper}>
        <BatchSummaryCards batch={batch} />
      </div>

      <div className={styles.detailMeta}>
        <span>
          <strong className={styles.detailMetaLabel}>Created At:</strong>{' '}
          {batch.startedAt
            ? `${parseMomentUtc(batch.startedAt)
                .local()
                .format(
                  'MM/DD/YYYY h:mm:ss A'
                )} ${getLocalTimezoneAbbreviation()}`
            : '--'}
        </span>
        <span>
          <strong className={styles.detailMetaLabel}>Duration:</strong>{' '}
          {elapsed}
        </span>
        <span>
          <strong className={styles.detailMetaLabel}>Progress:</strong>{' '}
          {batch.progressPercentage}%
        </span>
      </div>

      {/* ─── Filter Criteria ─── */}
      <FilterCriteria batch={batch} />

      <div className={styles.viewSwitcher}>
        <ContentSwitcher
          selectedIndex={viewIndex}
          className={styles.viewSwitcherControl}
          size="md"
          onChange={(e) => setViewIndex(e.index as number)}
        >
          <CarbonSwitch
            text="Batch View"
            className={styles.viewSwitcherButton}
          />
          <CarbonSwitch
            text="Document View"
            className={styles.viewSwitcherButton}
          />
        </ContentSwitcher>
      </div>

      {viewIndex === 0 ? (
        <SubmissionsTable batchId={batchId} />
      ) : (
        <AllDocumentsTable batchId={batchId} />
      )}

      <Modal
        open={showDeleteModal}
        danger
        modalHeading="Delete Batch"
        primaryButtonText={deleteState.loading ? 'Deleting...' : 'Delete'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={deleteState.loading}
        onRequestClose={() => setShowDeleteModal(false)}
        onSecondarySubmit={() => setShowDeleteModal(false)}
        onRequestSubmit={handleDelete}
      >
        <p>
          Are you sure you want to permanently delete Batch #{batchId}? This
          will remove the batch and all associated submissions and errors. This
          action cannot be undone.
        </p>
        {deleteState.error && (
          <InlineNotification
            kind="error"
            title="Delete failed"
            subtitle={deleteState.error}
            lowContrast
            hideCloseButton
          />
        )}
      </Modal>
    </>
  );
};
